import React, { useEffect, useMemo } from "react";
import OrderSort from "@/scenes/OrderManagement/enum/OrderSort";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopDTO from "@/dto/shop/ShopDTO";
import { Button, Divider, Modal, Select, Tooltip } from "antd";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import OrderSortNameMap, { OrderSortTipMap } from "@/map/OrderSortNameMap";
import OrderSortTipContent from "../OrderSortTipContent";
import { EditOutlined } from "@ant-design/icons";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import EditOrderSortModal from "../EditOrderSortModal";
import ShopAdvancedSettingModal from "../ShopAdvancedSettingButton/ShopAdvancedSettingModal";
import { generateOrderSortOptions } from "./util";
import { useOrderSetting } from "@/hooks/useOrderSetting";
import { UniversalOrderSettingType } from "@/services/api/universalOrderSetting";
import { OrderSortPrioritizationRule } from "@/services/api/orderSortPrioritizationSetting";
import type { BuildInPlacements } from "@rc-component/trigger/es/interface";

const orderSortSelectPlacements: BuildInPlacements = {
    bottomLeft: {
        points: ["tl", "bl"],
        offset: [0, 4],
        overflow: {
            adjustX: 1,
            adjustY: 0,
        },
        htmlRegion: "scroll",
    },
};

function useOrderSortOptions(tab: OrderTab, shop?: ShopDTO) {
    const { companyId } = useGlobalStore();

    const { data, save, loading } = useOrderSetting<OrderSortPrioritizationRule[]>(
        {
            companyId: shop ? null : companyId,
            shopId: shop ? shop.id : null,
            defaultValue: [],
            orderTab: tab,
            type: UniversalOrderSettingType.ORDER_SORT_PRIORITIZATION,
        },
        [shop, companyId, tab],
    );

    // 生成排序选项
    const orderSortOptions = useMemo(() => {
        return generateOrderSortOptions(tab, shop?.type, data);
    }, [shop, tab, data]);

    return { orderSortOptions, loading, handleSaveSortOptions: save };
}

// ================== 订单排序选择组件 ==================

interface OrderSortSelectProps {
    shop?: ShopDTO;
    tab: OrderTab;
}

/**
 * 订单列表排序方式选择组件。
 */
const OrderSortSelect: React.FC<OrderSortSelectProps> = function (props) {
    const { tab, shop } = props;
    const orderManagementStore = useOrderManagementStore();
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();

    // 获取排序选项和加载状态
    const { orderSortOptions, loading, handleSaveSortOptions } = useOrderSortOptions(tab, shop);

    useEffect(() => {
        /** 切换店铺时，判断是否需要提示开启颜色尺码排序设置 */

        if (!shop?.id) {
            return;
        }

        /**
         * 确保开启颜色尺码排序设置
         * 如果从A店铺切换到B店铺的时候，订单排序方式为颜色尺码排序，但是没有开启颜色尺码排序设置，则提示开启
         */
        async function ensureEnabledColorSize() {
            if (orderManagementStore.table.listOptions.orderSort !== OrderSort.PRODUCT_SKU_COLOR_SIZE) {
                return;
            }

            /**
             * 如果排序选项中没有颜色尺码排序，则不提示
             * 目前这段逻辑是多余的，因为颜色尺码排序在每种类型的店铺中都存在
             */
            if (!orderSortOptions.includes(OrderSort.PRODUCT_SKU_COLOR_SIZE)) return;

            const shopAdvancedSetting = await globalStore.shopAdvancedSettingStore.ensureAdvancedSetting(shop.id);
            if (shopAdvancedSetting?.enabledOrderItemColorSizeSorting) return;

            showEnabledOrderItemColorSizeSortingModal(shop.id);
        }

        ensureEnabledColorSize();
    }, [shop?.id]);

    function showEnabledOrderItemColorSizeSortingModal(shopId) {
        Modal.info({
            title: "请在高级设置中先勾选“开启自动识别商品属性颜色尺码”",
            okCancel: true,
            onOk() {
                modalStore.openModal(ShopAdvancedSettingModal, { shopId });
            },
        });
    }

    // 当前选中的排序方式
    const selectedOrderSort =
        orderManagementStore?.table?.listOptions?.orderSort ||
        (orderManagementStore.tab !== OrderTab.ABNORMAL
            ? OrderSort.ORDER_TIME_DESC
            : OrderSort.ABNORMAL_RECORD_TIME_DESC);

    async function handleOrderSortChange(orderSort: OrderSort) {
        orderManagementStore.table.changePageNo(1);
        if (shop) {
            const shopId = shop.id;

            if (orderSort === OrderSort.PRODUCT_SKU_COLOR_SIZE) {
                const shopAdvancedSetting = await globalStore.shopAdvancedSettingStore.getAdvancedSetting(shopId);

                if (!shopAdvancedSetting?.enabledOrderItemColorSizeSorting) {
                    showEnabledOrderItemColorSizeSortingModal(shopId);

                    return;
                }
            }

            orderManagementStore.table.changeOrderSort(shopId, tab, orderSort);
            orderManagementStore.fetchOrders(shopId, tab);
        } else {
            orderManagementStore.table.changeOrderSort(undefined, orderManagementStore.tab, orderSort);
            orderManagementStore.fetchOrders(undefined, undefined);
        }
    }

    const handleOpenEditOrderSortModal = () => {
        modalStore.openModal(EditOrderSortModal, {
            orderSortOptions,
            onSave: handleSaveSortOptions,
        });
    };

    return (
        <Select
            classNames={{ popup: { root: "ec-select-show-all-options" } }}
            style={{ fontSize: 12 }}
            size="small"
            value={selectedOrderSort}
            onChange={handleOrderSortChange}
            popupMatchSelectWidth={false}
            placement="bottomLeft"
            builtinPlacements={orderSortSelectPlacements}
            optionLabelProp="optionLabel"
            id="order-management-sort-select"
            loading={loading}
            popupRender={(menu) => {
                return (
                    <div>
                        <div>{menu}</div>
                        <Divider style={{ margin: "8px" }} />
                        <div style={{ margin: "0 10px" }}>
                            <Button
                                icon={<EditOutlined />}
                                type="link"
                                size="small"
                                onClick={handleOpenEditOrderSortModal}
                            >
                                <span style={{ margin: 0 }}>修改排序位置</span>
                            </Button>
                        </div>
                    </div>
                );
            }}
            options={[
                ...(orderSortOptions.map((orderSort) => {
                    const label = OrderSortNameMap[orderSort];
                    const tips = OrderSortTipMap[orderSort];
                    let el: React.ReactNode = label;
                    if (tips) {
                        const rule = shop ? tips.rule : `先按店铺排序，再${tips.rule}`;
                        el = (
                            <Tooltip
                                color="white"
                                destroyOnHidden
                                styles={{
                                    root: {
                                        maxWidth: 320,
                                    },
                                }}
                                title={<OrderSortTipContent title={label} rule={rule} eg={tips.eg} for={tips.for} />}
                                placement="left"
                            >
                                {el}
                            </Tooltip>
                        );
                    }
                    return {
                        key: orderSort,
                        value: orderSort,
                        className: "pr",
                        label: (
                            <>
                                {el}
                                {orderSort === OrderSort.ORDER_ITEMS && (
                                    <span
                                        style={{
                                            fontStyle: "italic",
                                            fontSize: 12,
                                            padding: "0 5px",
                                            background: "#eb9316",
                                            color: "#fff",
                                            marginLeft: "5px",
                                            borderRadius: "3px",
                                        }}
                                    >
                                        Hot
                                    </span>
                                )}
                            </>
                        ),
                        optionLabel: label,
                    };
                }) ?? []),
            ]}
        />
    );
};

export default observer(OrderSortSelect);
